import React, { useState, useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack
} from '@mui/material';
import { Task } from '../../types/shared';
import { getLocalDateString } from '../../utils/dateUtils';

interface TaskFormModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: TaskFormData) => Promise<void>;
    initialData?: Partial<Task> | null;
    isSubmitting?: boolean;
    title?: string;
}

export interface TaskFormData {
    title: string;
    description: string;
    date: string;
    priority: number;
    plantIds: string[];
    seedId: string | null;
}

const TaskFormModal: React.FC<TaskFormModalProps> = ({
    open,
    onClose,
    onSubmit,
    initialData,
    isSubmitting = false,
    title = 'Add Manual Task'
}) => {
    const [formData, setFormData] = useState<TaskFormData>({
        title: '',
        description: '',
        date: getLocalDateString(),
        priority: 2,
        plantIds: [],
        seedId: null
    });

    useEffect(() => {
        if (open && initialData) {
            setFormData({
                title: initialData.title || '',
                description: initialData.description || '',
                date: initialData.date || getLocalDateString(),
                priority: initialData.priority || 2,
                plantIds: initialData.plantIds || [],
                seedId: initialData.seedId || null
            });
        } else if (open && !initialData) {
            // Reset form when opening new
            setFormData({
                title: '',
                description: '',
                date: getLocalDateString(),
                priority: 2,
                plantIds: [],
                seedId: null
            });
        }
    }, [open, initialData]);

    const handleSubmit = async () => {
        await onSubmit(formData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        autoFocus
                        label="Task Title"
                        fullWidth
                        variant="outlined"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    />
                    <TextField
                        label="Description (Optional)"
                        fullWidth
                        multiline
                        rows={2}
                        variant="outlined"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                    <TextField
                        label="Date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    />
                    <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select
                            value={formData.priority}
                            label="Priority"
                            onChange={(e) => setFormData({ ...formData, priority: Number(e.target.value) })}
                        >
                            <MenuItem value={1}>High (1)</MenuItem>
                            <MenuItem value={2}>Medium (2)</MenuItem>
                            <MenuItem value={3}>Low (3)</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
                    {initialData ? 'Update Task' : 'Add Task'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TaskFormModal;
